import React from "react";
import { Send, CheckCircle2, MailOpen, MousePointerClick, AlertTriangle, UserX, TrendingUp, TrendingDown } from "lucide-react";
import Skeleton from "components/ui/Skeleton";
import { fmtCompact } from "../utils";

const TILE_STYLE = {
  primary: { bg: "bg-primary_tint", text: "text-primary", border: "border-primary/20" },
  secondary: { bg: "bg-secondary_tint", text: "text-secondary", border: "border-secondary/20" },
  success: { bg: "bg-success_bg", text: "text-success", border: "border-success_border" },
  info: { bg: "bg-info_bg", text: "text-info", border: "border-info_border" },
  danger: { bg: "bg-danger_bg", text: "text-danger", border: "border-danger_border" },
  warning: { bg: "bg-warning_bg", text: "text-warning", border: "border-warning_border" },
};

const DeltaBadge = ({ value, invert = false }) => {
  if (value === null || value === undefined) return null;
  const rising = value >= 0;
  const good = invert ? !rising || value === 0 : rising;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-medium px-1.5 py-0.5 rounded-md ${good ? "text-success bg-success_bg" : "text-danger bg-danger_bg"}`}>
      {rising ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
      {Math.abs(value)}%
    </span>
  );
};

const Tile = ({ icon: Icon, label, value, sub, variant, delta, invert }) => {
  const s = TILE_STYLE[variant];
  return (
    <div className="rounded-xl border border-border bg-surface p-4">
      <div className="flex items-start justify-between mb-3">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border ${s.bg} ${s.border}`}>
          <Icon size={18} className={s.text} />
        </div>
        <DeltaBadge value={delta} invert={invert} />
      </div>
      <p className="text-xl font-bold text-text_primary tabular-nums">{value}</p>
      <p className="text-xs text-text_secondary mt-0.5">{label}</p>
      {sub && <p className="text-[11px] text-text_muted mt-1">{sub}</p>}
    </div>
  );
};

const StatTiles = ({ summary, deltas, loading }) => {
  if (loading) {
    return <Skeleton.Tiles count={6} className="grid-cols-2 sm:grid-cols-3 lg:grid-cols-6" />;
  }

  const s = summary || {};
  const d = deltas || {};

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
      <Tile icon={Send} label="Emails Sent" value={fmtCompact(s.total_sent)} sub={`${s.total_campaigns ?? 0} campaigns`} variant="primary" delta={d.total_sent} />
      <Tile icon={CheckCircle2} label="Delivered" value={fmtCompact(s.total_delivered)} sub={`${s.delivery_rate ?? 0}% rate`} variant="secondary" delta={d.total_delivered} />
      <Tile icon={MailOpen} label="Opened" value={fmtCompact(s.total_opened)} sub={`${s.open_rate ?? 0}% open rate`} variant="success" delta={d.open_rate} />
      <Tile icon={MousePointerClick} label="Clicked" value={fmtCompact(s.total_clicked)} sub={`${s.click_rate ?? 0}% CTR`} variant="info" delta={d.click_rate} />
      <Tile icon={AlertTriangle} label="Bounced" value={fmtCompact(s.total_bounced)} sub={`${s.bounce_rate ?? 0}% rate`} variant="danger" delta={d.total_bounced} invert />
      <Tile icon={UserX} label="Unsubscribed" value={fmtCompact(s.total_unsubscribed)} sub={`${s.unsubscribe_rate ?? 0}% rate`} variant="warning" />
    </div>
  );
};

export default StatTiles;
